import { useState } from 'react';
import styled from 'styled-components';
import { Pencil, Sparkles, ImagePlus, Printer } from 'lucide-react';
import AvatarIcon from './AvatarIcon';

export default function MessageBubble({ message, currentUserId, memberInfo, onDrawOnThis, onEditWithRoss, onEditWithRossPrefill, onSaveToGallery, isLatestArtwork, activeGame, currentCircle }) {
  const [savedToGallery, setSavedToGallery] = useState(false);
  const isRoss = message.role === 'assistant';
  const isMe = message.role === 'user' && message.userId === currentUserId;
  const isOtherUser = message.role === 'user' && message.userId !== currentUserId;
  const variant = isMe ? 'me' : isOtherUser ? 'other' : 'ross';

  const resolvedName = isOtherUser
    ? (memberInfo?.[message.userId]?.displayName || message.displayName || '?')
    : (message.displayName || (isRoss ? 'Ross' : '?'));

  const handleSaveToGallery = () => {
    onSaveToGallery?.();
    setSavedToGallery(true);
  };

  const showSaveToGallery = isLatestArtwork && message.imageUrl && message.isNewArtwork;

  return (
    <Row $variant={variant}>
      {isRoss && <AvatarIcon name="Ross" photoURL="/ross.png" size={32} />}
      {isOtherUser && <AvatarIcon name={resolvedName} size={32} />}
      <Bubble $variant={variant}>
        {!isMe && <SenderName>{resolvedName}</SenderName>}
        {message.text && <Text>{message.text}</Text>}
        {message.imageUrl && (
          <ImageWrapper>
            <ArtImage src={message.imageUrl} alt="Art" loading="lazy" />
            {message.isNewArtwork && (
              <ImageActions>
                <DrawOnThisButton onClick={() => onDrawOnThis?.(message.fullResImageUrl || message.imageUrl)}>
                  <Pencil size={14} />
                  Draw on this
                </DrawOnThisButton>
                <EditWithAIButton onClick={() => onEditWithRossPrefill?.(message.fullResImageUrl || message.imageUrl)}>
                  <Sparkles size={14} />
                  Edit with AI
                </EditWithAIButton>
                <OrderPrintButton onClick={() => window.open(`https://app.domoreworld.com/customize-and-order?imageUrl=${encodeURIComponent(message.fullResImageUrl || message.imageUrl)}`, '_blank')}>
                  <Printer size={14} />
                  Order Print
                </OrderPrintButton>
                {showSaveToGallery && (
                  savedToGallery ? (
                    <SaveToGalleryButton disabled>
                      <ImagePlus size={14} />
                      Saved!
                    </SaveToGalleryButton>
                  ) : (
                    <SaveToGalleryButton onClick={handleSaveToGallery}>
                      <ImagePlus size={14} />
                      Save to Gallery
                    </SaveToGalleryButton>
                  )
                )}
              </ImageActions>
            )}
          </ImageWrapper>
        )}
        <Timestamp>{formatTime(message.timestamp)}</Timestamp>
      </Bubble>
    </Row>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  const date = typeof ts === 'number' ? new Date(ts) : ts.toDate?.() || new Date(ts);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  justify-content: ${({ $variant }) => ($variant === 'me' ? 'flex-end' : 'flex-start')};
  padding: 2px 0;
`;

const bubbleBg = ({ $variant, theme }) => {
  if ($variant === 'me') return '#3B82F6';
  if ($variant === 'other') return theme.colors.accent;
  return theme.colors.surface;
};

const bubbleColor = ({ $variant, theme }) => {
  if ($variant === 'me' || $variant === 'other') return '#fff';
  return theme.colors.textPrimary;
};

const Bubble = styled.div`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.large};
  border-bottom-${({ $variant }) => ($variant === 'me' ? 'right' : 'left')}-radius: 4px;
  background: ${bubbleBg};
  color: ${bubbleColor};
`;

const SenderName = styled.div`
  font-size: 13px;
  font-weight: 600;
  opacity: 0.7;
  margin-bottom: 2px;
`;

const Text = styled.p`
  font-size: 16px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ImageWrapper = styled.div`
  margin-top: 8px;
`;

const ArtImage = styled.img`
  border-radius: ${({ theme }) => theme.radius.medium};
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
`;

const ImageActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const pillBase = `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  min-height: 40px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.8;
  }
`;

const DrawOnThisButton = styled.button`
  ${pillBase}
  color: #f59e0b;
  background: white;

  &:hover:not(:disabled) {
    background: #fffbeb;
  }
`;

const EditWithAIButton = styled.button`
  ${pillBase}
  color: ${({ theme }) => theme.colors.textSecondary || theme.colors.textPrimary};
  background: white;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }
`;

const OrderPrintButton = styled.button`
  ${pillBase}
  color: #8b5cf6;
  background: white;

  &:hover:not(:disabled) {
    background: #faf5ff;
  }
`;

const SaveToGalleryButton = styled.button`
  ${pillBase}
  color: #059669;
  background: white;

  &:hover:not(:disabled) {
    background: #f0fdf4;
  }
`;

const Timestamp = styled.div`
  font-size: 13px;
  opacity: 0.5;
  margin-top: 4px;
  text-align: right;
`;
